import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import Staff from "@/components/Staff";
import { buildLetterSequence } from "@/lib/composition";
import { buildHebrewSequence } from "@/lib/hebrewComposition";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fantasia ghost palette — matches the ghost letters + smoke splats
// used on the Fantasia display, so the recap collage rhymes visually
// with the rest of the show.
const NOTE_COLORS = [
  { bg: "#F2D589", ink: "#3a2b0a" }, // gold
  { bg: "#FCA07A", ink: "#3a1e12" }, // coral
  { bg: "#E86A92", ink: "#3a0f1f" }, // rose
  { bg: "#B79CE8", ink: "#26163d" }, // lavender
  { bg: "#A774FF", ink: "#0f2f30" }, // seafoam
  { bg: "#FFB86B", ink: "#3a1f04" }, // amber
  { bg: "#7BC4F2", ink: "#0e2536" }, // sky
  { bg: "#FF7EB6", ink: "#3a0e26" }, // pink
];

// Items whose text is a "musical word" — anything the audience or Dan
// wrote (as opposed to a static scene label). These items trigger a
// staff overlay in the middle of the collage while they're the current
// note.
const MUSIC_KINDS = new Set(["location", "parsha", "audience"]);
// Cadence for the mini staff's note-by-note reveal on each post-it.
const MINI_STAFF_STEP_MS = 420;
// Deterministic RNG (mulberry32) seeded from a string. Ensures a given
// item id always gets the same color/rotation/jitter across renders and
// reloads, so the collage doesn't "reshuffle" while Dan is playing the
// recap.
function seededRng(seedStr) {
  let h = 1779033703 ^ seedStr.length;
  for (let i = 0; i < seedStr.length; i += 1) {
    h = Math.imul(h ^ seedStr.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  let a = h >>> 0;
  return () => {
    a = (a + 0x6D2B79F5) | 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Compute a stable per-item layout on a 10-column grid (each note spans
 * 2 columns = one "slot"). The middle block (cols 3-8, rows 2-3) is
 * reserved for the framed scene title. Rows 1 and 4 hold 5 slots each,
 * rows 2 and 3 hold one slot on either side. A partially-filled 5-slot
 * row is CENTERED (4 notes → colStarts 2,4,6,8). Each note also gets a
 * deterministic rotation ±10°, small jitter, and a palette color.
 */
function useCollageLayout(items) {
  return useMemo(() => {
    const rows = [
      { row: 1, colStarts: [1, 3, 5, 7, 9], wide: true },
      { row: 2, colStarts: [1, 9], wide: false },
      { row: 3, colStarts: [1, 9], wide: false },
      { row: 4, colStarts: [1, 3, 5, 7, 9], wide: true },
    ];
    const slots = [];
    rows.forEach((r) => r.colStarts.forEach((c) => slots.push({ row: r.row, colStart: c, wide: r.wide })));

    const placed = items.map((item, idx) => ({ item, idx, slot: slots[idx % slots.length] }));

    // Centre any wide row that didn't fill all 5 slots (one unused slot
    // = 2 grid columns, so shifting by half a slot = +1 column each).
    const usedPerRow = placed.reduce((acc, p) => {
      acc[p.slot.row] = (acc[p.slot.row] || 0) + 1;
      return acc;
    }, {});

    return placed.map(({ item, idx, slot }, i, all) => {
      const rng = seededRng(item.id || `i${idx}`);
      // Alternate the tilt direction so no two notes in a row lean the
      // same way, with a seeded magnitude for variety.
      const rotation = (idx % 2 === 0 ? 1 : -1) * (4 + rng() * 6);
      const jitterX = (rng() - 0.5) * 8;
      const jitterY = (rng() - 0.5) * 8;
      const used = usedPerRow[slot.row] || 0;
      const shift = slot.wide && used < 5 ? 5 - used : 0;
      const colStart = slot.colStart + shift;

      // Pick a colour that differs from the note to the left and the note
      // directly above, so identical colours never sit side by side.
      const taken = new Set();
      const left = all[i - 1];
      if (left && left.assignedColor && left.slot.row === slot.row) taken.add(left.assignedColor);
      const above = all.find(
        (p) => p.assignedColor && p.slot.row === slot.row - 1 && p.assignedColor && p.colStart === colStart,
      );
      if (above) taken.add(above.assignedColor);
      const start = Math.floor(rng() * NOTE_COLORS.length);
      let color = NOTE_COLORS[start];
      for (let k = 0; k < NOTE_COLORS.length; k += 1) {
        const candidate = NOTE_COLORS[(start + k) % NOTE_COLORS.length];
        if (!taken.has(candidate)) {
          color = candidate;
          break;
        }
      }
      all[i].assignedColor = color;
      all[i].colStart = colStart;

      return {
        ...item,
        row: slot.row,
        colStart,
        rotation,
        jitterX,
        jitterY,
        color,
      };
    });
  }, [items]);
}

/**
 * Wall-clock tick — re-renders the collage every ~300ms while playback
 * is running so time-derived state (revealed count + FIN threshold)
 * stays fresh even after the reveal count settles. Without this the
 * FIN transition would never fire, because after all items are on
 * screen no state changes and time-comparison memos stay cached at
 * their pre-FIN values.
 */
function useNowTick(startedAt) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!startedAt) { setNow(Date.now()); return undefined; }
    const id = window.setInterval(() => setNow(Date.now()), 300);
    return () => window.clearInterval(id);
  }, [startedAt]);
  return now;
}

export default function RecapCollage({ slug, slide, startedAt, stepMs }) {
  const [items, setItems] = useState([]);

  // Keep the collage in sync with the DB: refetch on every play kick-off
  // (so last-second audience picks land) and poll while the slide is up,
  // with a cache-buster because a stale 304/proxy hit would otherwise
  // freeze the last audience word.
  useEffect(() => {
    if (!slug) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/live/${slug}/recap`, {
          params: { t: Date.now() },
          headers: { "Cache-Control": "no-cache" },
        });
        if (!cancelled) setItems(data.items || []);
      } catch {
        if (!cancelled) setItems([]);
      }
    };
    load();
    // Only poll while playback is idle — once Dan hits Play the item list
    // is frozen so the reveal cadence can't shift under itself.
    const id = startedAt ? null : window.setInterval(load, 10000);
    return () => {
      cancelled = true;
      if (id) window.clearInterval(id);
    };
  }, [slug, startedAt]);

  const laid = useCollageLayout(items);

  // Time-derived state — recomputed every tick, no caching. `revealed`
  // is the count of sticky notes currently on stage; `finRevealed`
  // flips true 15 seconds after the finale reveals (i.e. at
  // laid.length * step_ms after play start).
  const now = useNowTick(startedAt);
  const step = Math.max(1000, stepMs || 15000);
  const startMs = startedAt ? new Date(startedAt).getTime() : null;
  const revealed = !startMs
    ? 0
    : Math.max(0, Math.min(laid.length, Math.floor((now - startMs) / step) + 1));
  const finRevealed = !!startMs && laid.length > 0 && (now - startMs) >= laid.length * step;

  const headline = slide?.headline || "Tonight's Composition";
  const subhead = slide?.subhead || "";

  // The "current" music-word note is the most-recently-revealed item
  // that has a musical word (location/parsha/audience). Its staff
  // overlays the framed title. When the next note revealed is a scene
  // (non-music) or the finale, the staff clears and the title returns.
  const currentMusicItem = useMemo(() => {
    if (revealed <= 0) return null;
    const latest = laid[revealed - 1];
    if (!latest) return null;
    return MUSIC_KINDS.has(latest.kind) ? latest : null;
  }, [laid, revealed]);

  return (
    <div
      className="relative w-full h-full min-h-screen"
      data-testid="slide-recap-collage"
      data-revealed={revealed}
      data-fin={finRevealed ? "1" : "0"}
    >
      <AnimatePresence>
        {!finRevealed ? (
          <motion.div
            key="collage"
            initial={false}
            exit={{ opacity: 0 }}
            transition={{ duration: 2.4, ease: "easeInOut" }}
            className="absolute inset-0"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(10, minmax(0, 1fr))",
              gridTemplateRows: "repeat(4, minmax(0, 1fr))",
              padding: "clamp(24px, 4vw, 64px) clamp(24px, 5vw, 88px)",
              gap: 0,
            }}
          >
            {laid.map((it, idx) => (
              <RecapNoteCell
                key={it.id || idx}
                item={it}
                visible={idx < revealed}
              />
            ))}

            {/* Center title tile — spans the reserved 3×2 block. When a
                music-word note is "current", the staff overlays the title
                (title stays underneath so the transition back is instant). */}
            <div
              className="flex flex-col items-center justify-center text-center relative z-10"
              style={{
                gridColumn: "3 / span 6",
                gridRow: "2 / span 2",
                padding: "clamp(16px, 2vw, 32px)",
              }}
              data-testid="slide-recap-title"
            >
              <AnimatePresence mode="wait">
                {currentMusicItem ? (
                  <motion.div
                    key={`staff-${currentMusicItem.id}`}
                    initial={{ opacity: 0, scale: 0.92 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.92 }}
                    transition={{ duration: 0.45, ease: "easeOut" }}
                    className="w-full flex flex-col items-center"
                    data-testid="slide-recap-staff"
                  >
                    <div
                      className="text-[10px] sm:text-xs mb-3"
                      style={{ color: currentMusicItem.color.bg, letterSpacing: "0.32em", textTransform: "uppercase", fontWeight: 600 }}
                    >
                      {currentMusicItem.sub}
                    </div>
                    <MiniStaff
                      item={currentMusicItem}
                      sinceMs={startMs ? startMs + (revealed - 1) * step : null}
                      windowMs={step}
                    />
                    <div
                      className="mt-3"
                      style={{
                        color: "#F5EFE3",
                        fontFamily: currentMusicItem.mode === "hebrew"
                          ? "'Frank Ruhl Libre', 'Cormorant Garamond', serif"
                          : "'Cormorant Garamond', serif",
                        fontSize: "clamp(20px, 2vw, 34px)",
                        direction: currentMusicItem.mode === "hebrew" ? "rtl" : "ltr",
                        textShadow: "0 0 24px rgba(5,2,17,0.9)",
                      }}
                    >
                      {currentMusicItem.text}
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    key="title-default"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4 }}
                    className="flex flex-col items-center"
                  >
                    <div
                      className="text-sm sm:text-base mb-4"
                      style={{ color: "#F2D589", letterSpacing: "0.32em", textTransform: "uppercase", fontWeight: 600 }}
                    >
                      {slide?.scene_label || "Act 3 – Scene 3"}
                    </div>
                    <h2
                      className="leading-tight"
                      style={{
                        fontFamily: "'Frank Ruhl Libre', 'Cormorant Garamond', serif",
                        fontWeight: 500,
                        color: "#F5EFE3",
                        fontSize: "clamp(40px, 5.5vw, 96px)",
                        textShadow: "0 0 60px rgba(5,2,17,0.9), 0 0 24px rgba(5,2,17,0.9)",
                      }}
                    >
                      {headline}
                    </h2>
                    {subhead ? (
                      <p
                        className="mt-4"
                        style={{
                          color: "rgba(245,239,227,0.72)",
                          fontFamily: "'Cormorant Garamond', serif",
                          fontSize: "clamp(16px, 1.4vw, 22px)",
                          textShadow: "0 0 20px rgba(5,2,17,0.9)",
                        }}
                      >
                        {subhead}
                      </p>
                    ) : null}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="fin"
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 2.6, ease: [0.19, 1, 0.22, 1], delay: 1.2 }}
            className="absolute inset-0 flex items-center justify-center"
            data-testid="slide-recap-fin"
          >
            <div
              style={{
                fontFamily: "'Frank Ruhl Libre', 'Cormorant Garamond', serif",
                fontWeight: 400,
                letterSpacing: "0.12em",
                color: "#F5EFE3",
                fontSize: "clamp(160px, 22vw, 400px)",
                lineHeight: 1,
                textShadow: "0 0 120px rgba(232,196,138,0.35), 0 0 60px rgba(5,2,17,0.9)",
              }}
            >
              FIN
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/**
 * Wraps a note so we control mount timing via the parent's `visible`
 * boolean. Once revealed, the note stays mounted for the rest of the
 * collage (no re-shuffle). The wrapper occupies its grid cell whether
 * or not the note has been revealed so the layout is stable.
 */
function RecapNoteCell({ item, visible }) {
  return (
    <div
      style={{
        gridColumn: `${item.colStart} / span 2`,
        gridRow: `${item.row} / span 1`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "8px",
        position: "relative",
      }}
    >
      <AnimatePresence>
        {visible ? <RecapNote item={item} /> : null}
      </AnimatePresence>
    </div>
  );
}

function RecapNote({ item }) {
  const isHebrew = item.mode === "hebrew";
  const isFinale = item.kind === "finale";
  return (
    <motion.div
      initial={{ opacity: 0, y: 40, rotate: 0, scale: 0.85 }}
      animate={{
        opacity: 1,
        y: 0,
        rotate: item.rotation,
        scale: isFinale ? 1.08 : 1,
        x: `${item.jitterX}%`,
      }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{
        type: "spring",
        stiffness: 90,
        damping: 12,
      }}
      style={{
        backgroundColor: isFinale ? "#F5EFE3" : item.color.bg,
        color: isFinale ? "#080419" : item.color.ink,
        width: "min(100%, 220px)",
        aspectRatio: "1 / 1",
        padding: "clamp(10px, 1.2vw, 18px)",
        boxShadow: isFinale
          ? "0 30px 60px rgba(232,196,138,0.35), 0 8px 20px rgba(0,0,0,0.5)"
          : "0 20px 40px rgba(0,0,0,0.55), 0 6px 12px rgba(0,0,0,0.35)",
        borderRadius: "6px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        textAlign: "center",
        overflow: "hidden",
        willChange: "transform",
      }}
      data-testid={`slide-recap-note-${item.id}`}
      data-kind={item.kind}
    >
      {item.top_label ? (
        <div
          className="mb-1"
          style={{
            fontFamily: "'Courier Prime', monospace",
            fontSize: "clamp(8px, 0.6vw, 10px)",
            opacity: 0.7,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            direction: "ltr",
          }}
          data-testid="slide-recap-note-top"
        >
          {item.top_label}
        </div>
      ) : null}
      <div
        style={{
          fontFamily: isHebrew
            ? "'Frank Ruhl Libre', 'Cormorant Garamond', serif"
            : "'Cormorant Garamond', serif",
          fontWeight: isFinale ? 500 : 500,
          fontStyle: isFinale ? "italic" : "normal",
          fontSize: isFinale ? "clamp(18px, 1.6vw, 28px)" : "clamp(15px, 1.4vw, 24px)",
          lineHeight: 1.1,
          direction: isHebrew ? "rtl" : "ltr",
          wordBreak: "break-word",
          overflowWrap: "anywhere",
        }}
        data-testid="slide-recap-note-text"
      >
        {item.text}
      </div>
      {(item.dots || []).length ? (
        <div className="mt-2 flex items-center gap-2" data-testid="slide-recap-note-dots">
          {item.dots.map((c) => (
            <span
              key={c}
              style={{
                width: "clamp(12px, 1.1vw, 20px)",
                height: "clamp(12px, 1.1vw, 20px)",
                borderRadius: "999px",
                backgroundColor: c,
                boxShadow: `0 0 10px ${c}`,
              }}
            />
          ))}
        </div>
      ) : null}
      {item.image_url ? (
        <img
          src={item.image_url}
          alt=""
          className="mt-2"
          style={{ maxHeight: "clamp(28px, 3vw, 52px)", maxWidth: "82%", objectFit: "contain" }}
          data-testid="slide-recap-note-image"
        />
      ) : null}
      {(item.notes || []).length ? (
        <div
          className="mt-2 flex flex-wrap gap-2"
          style={{ direction: "ltr" }}
          data-testid="slide-recap-note-pitches"
        >
          {item.notes.map((n, i) => (
            <span
              key={`${n}-${i}`}
              style={{
                fontFamily: "'Frank Ruhl Libre', serif",
                fontSize: "clamp(14px, 1.2vw, 20px)",
                fontWeight: 600,
                letterSpacing: "0.04em",
              }}
            >
              {n}
            </span>
          ))}
        </div>
      ) : null}
      {item.sub ? (
        <div
          className="mt-2"
          style={{
            fontFamily: "'Courier Prime', monospace",
            fontSize: "clamp(9px, 0.7vw, 11px)",
            opacity: 0.75,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            direction: "ltr",
          }}
          data-testid="slide-recap-note-sub"
        >
          {item.kind === "audience" ? `— ${item.sub}` : item.sub}
        </div>
      ) : null}
    </motion.div>
  );
}

/**
 * Compact staff render of a single item's text. Uses the same
 * buildLetterSequence / buildHebrewSequence helpers as the main Live
 * Display so the notation is identical to what the audience saw
 * during the show.
 */
function MiniStaff({ item, sinceMs, windowMs }) {
  const dropped = useMemo(() => {
    if (!item?.text) return [];
    const words = item.text.split(/\s+/).filter(Boolean);
    if (!words.length) return [];
    if (item.mode === "hebrew") {
      return buildHebrewSequence(words, { root: "C", modeIdx: 0 });
    }
    return buildLetterSequence(words);
  }, [item?.text, item?.mode]);

  // Notes land one at a time off a wall-clock reference (the moment this
  // post-it was revealed), so a display joining mid-recap stays in sync.
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    setNow(Date.now());
    const id = window.setInterval(() => setNow(Date.now()), 120);
    return () => window.clearInterval(id);
  }, [sinceMs, item?.id]);

  if (!dropped.length) return null;

  // Squeeze the cadence if the post-it's window is too short to fit
  // every note at the default pace.
  const stepMs = Math.min(
    MINI_STAFF_STEP_MS,
    Math.max(70, ((windowMs || 15000) * 0.7) / dropped.length),
  );
  const count = sinceMs
    ? Math.max(1, Math.min(dropped.length, Math.floor((now - sinceMs) / stepMs) + 1))
    : dropped.length;
  const shown = dropped.slice(0, count);

  return (
    <div style={{ width: "100%" }}>
      <Staff
        droppedEntries={shown}
        activeIndex={shown.length - 1}
        fluid
        maxHeight="30vh"
        reserveEntries={dropped}
      />
    </div>
  );
}
